import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCopy,
  faMobileAlt,
  faTruck,
  faSearch,
  faInfoCircle,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
import styles from "../../styles/components/OrderSuccessCard.module.css";

interface OrderSuccessCardProps {
  trackingNumber: string;
  upsTrackingNumber?: string;
  carrierName: string;
  carrierBase64?: string;
  externalServiceBase64?: string;
  externalServices?: any[];
  receiverName: string;
  destination: string;
  weight: string;
  content: string;
  hasSecondaryLabel?: boolean;
  onNewShipment?: () => void;
  idOrder?: number;
  base64Pdf?: string;
}

const OrderSuccessCard: React.FC<OrderSuccessCardProps> = ({
  trackingNumber,
  upsTrackingNumber,
  carrierName,
  carrierBase64,
  externalServiceBase64,
  externalServices,
  receiverName,
  destination,
  weight,
  content,
  hasSecondaryLabel = false,
  onNewShipment,
  idOrder,
  base64Pdf,
}) => {
  const displayTracking = upsTrackingNumber || trackingNumber;
  const displayLabel = upsTrackingNumber ? "Courier" : (carrierName || "Envío");

  const downloadPdf = (base64: string, fileName: string) => {
    try {
      const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;

      const linkSource = `data:application/pdf;base64,${base64Data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al intentar descargar el documento.",
      });
    }
  };

  const getAllGuideLabels = () => {
    const list: { base64: string; fileName: string }[] = [];

    if (carrierBase64) {
      list.push({ base64: carrierBase64, fileName: `Guia_${trackingNumber}.pdf` });
    }

    if (externalServices && Array.isArray(externalServices)) {
      externalServices.forEach((ext: any) => {
        if (ext.packages && Array.isArray(ext.packages) && ext.packages.length > 0) {
          ext.packages.forEach((pkg: any) => {
            const base64 = pkg.labelBase64 || pkg.serviceBase64 || ext.serviceBase64;
            const tracking = pkg.trackingNumber || ext.serviceTrackingNumber || displayTracking;
            if (base64 && !list.some((item) => item.base64 === base64)) {
              list.push({ base64, fileName: `Guia_${tracking}.pdf` });
            }
          });
        } else if (ext.serviceBase64) {
          const tracking = ext.serviceTrackingNumber || displayTracking;
          if (!list.some((item) => item.base64 === ext.serviceBase64)) {
            list.push({ base64: ext.serviceBase64, fileName: `Guia_${tracking}.pdf` });
          }
        }
      });
    } else if (externalServiceBase64) {
      if (!list.some((item) => item.base64 === externalServiceBase64)) {
        list.push({ base64: externalServiceBase64, fileName: `Guia_${displayTracking}.pdf` });
      }
    }

    return list;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        title: "¡Copiado!",
        text: "Número de tracking copiado al portapapeles",
        icon: "success",
        confirmButtonColor: "var(--color-primary)",
        confirmButtonText: "Entendido",
      });
    });
  };

  return (
    <div className={`${styles.orderSuccessCard} fade-in`}>
      <div className={styles.successIcon}>
        <FontAwesomeIcon icon={faCheckCircle} />
      </div>
      <h3 className={styles.title}>¡Recogida Confirmada!</h3>
      <p className={styles.successMessage}>
        Tu envío ha sido procesado exitosamente.
      </p>

      <div className={styles.trackingDisplay}>
        <span className="label">
          Número de Tracking:
        </span>
        <div
          className={styles.trackingValueBox}
          onClick={() => copyToClipboard(displayTracking)}
          title="Click para copiar"
        >
          <span className="value">{displayTracking}</span>
          <FontAwesomeIcon icon={faCopy} className={styles.copyIconMini} />
        </div>

        <div className={styles.successInfoRow}>
          <div className={styles.qrContainer}>
            <QRCodeSVG
              value={`${window.location.origin}/track?tracking=${trackingNumber}`}
              size={120}
            />
            <span className={styles.qrLabel}>
              <FontAwesomeIcon
                icon={faMobileAlt}
                style={{ marginRight: "8px" }}
              />
              Seguimiento
            </span>
          </div>

          <div className={styles.shippingDetailsMini}>
            <div className={styles.miniDetail}>
              <span className={styles.miniLabel}>Destinatario</span>
              <span className={styles.miniValue}>{receiverName}</span>
            </div>
            <div className={styles.miniDetail}>
              <span className={styles.miniLabel}>Destino</span>
              <span className={styles.miniValue}>{destination}</span>
            </div>
            <div className={styles.miniDetail}>
              <span className={styles.miniLabel}>Peso / Contenido</span>
              <span className={styles.miniValue}>
                {weight} - {content}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.successActionsGrid}>
        {getAllGuideLabels().length > 0 && (
          <button
            type="button"
            className={`${styles.successActionBtn} ${styles.downloadBtn}`}
            onClick={() => {
              const labels = getAllGuideLabels();
              labels.forEach((item, index) => {
                setTimeout(() => {
                  downloadPdf(item.base64, item.fileName);
                }, index * 400);
              });
            }}
          >
            <FontAwesomeIcon icon={faTruck} /> Descargar Guía{getAllGuideLabels().length > 1 ? "s" : ""}
          </button>
        )}

        {base64Pdf && (
          <button
            type="button"
            className={`${styles.successActionBtn} ${styles.downloadBtn}`}
            onClick={() => downloadPdf(base64Pdf, `Invoice_${idOrder || trackingNumber}.pdf`)}
          >
            <FontAwesomeIcon icon={faFilePdf} /> Descargar Resumen
          </button>
        )}

        <button
          type="button"
          className={`${styles.successActionBtn} ${styles.detailsBtn}`}
          onClick={() =>
            window.open(
              `/instructive?showStep2=${hasSecondaryLabel}`,
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <FontAwesomeIcon icon={faInfoCircle} /> Instrucciones de etiquetas
        </button>

        <button
          type="button"
          className={`${styles.successActionBtn} ${styles.detailsBtn}`}
          onClick={() =>
            window.open(
              `/customer/order/${idOrder}`,
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <FontAwesomeIcon icon={faSearch} /> Ver Detalles del Envío
        </button>
      </div>

      {onNewShipment && (
        <button
          type="button"
          className={styles.newShipmentBtn}
          onClick={onNewShipment}
        >
          Nuevo Envío
        </button>
      )}

      <p style={{
        fontSize: "12px",
        color: "#6c757d",
        marginTop: "15px",
        textAlign: "center",
        lineHeight: "1.4"
      }}>
        Las dimensiones y el peso ingresados son de carácter verídico y están sujetos a recálculo y auditoría por parte del courier final.
      </p>
    </div>
  );
};

export default OrderSuccessCard;
